import React, { useRef } from "react";
import { Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import dayjs from "dayjs";
import { FaCheck } from "react-icons/fa";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useFieldStackSpacing } from "./fieldStackContext";

const dateTimePickerTheme = createTheme({
  typography: {
    fontFamily: '"Schibsted Grotesk", "Segoe UI", system-ui, sans-serif',
  },
});

const NewDateTimePicker = ({
  name,
  label,
  control,
  rules,
  errors,
  setValue,
  watch,
  minDate,
  maxDate,
  onMaxDateExceeded,
  trigger, // <-- add trigger prop
}) => {
  const hasAutoSetTime = useRef(false); // Track if auto-set time has occurred
  const isInFieldStack = useFieldStackSpacing();

  return (
    <div
      className={`tw-relative tw-w-full ${isInFieldStack ? "" : "tw-mb-[15px]"}`}
    >
      {label && (
        <label className="tw-mb-2 tw-block tw-text-base tw-font-bold tw-text-dis-navy">
          {label}
        </label>
      )}
      <div
        className={`tw-relative tw-w-full ${isInFieldStack ? "" : "tw-mt-2.5"}`}
      >
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <ThemeProvider theme={dateTimePickerTheme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                {...field}
                onChange={(date) => {
                  if (!date) {
                    setValue(name, "");
                    hasAutoSetTime.current = false;
                    if (trigger) trigger(name); // <-- trigger validation
                    return;
                  }

                  const selectedDate = dayjs(date);
                  const today = dayjs();

                  if (
                    onMaxDateExceeded &&
                    maxDate &&
                    selectedDate.isValid() &&
                    selectedDate.isAfter(dayjs(maxDate), "day")
                  ) {
                    setValue(name, "");
                    hasAutoSetTime.current = false;
                    if (trigger) trigger(name);
                    onMaxDateExceeded();
                    return;
                  }

                  let finalDateTime = selectedDate;

                  // Only auto-set time if today is selected AND we haven't set it before
                  if (
                    selectedDate.isSame(today, "day") &&
                    !hasAutoSetTime.current
                  ) {
                    finalDateTime = selectedDate
                      .hour(today.hour())
                      .minute(today.minute());
                    hasAutoSetTime.current = true;
                  }

                  // Reset auto-set flag if user picks a non-today date
                  if (!selectedDate.isSame(today, "day")) {
                    hasAutoSetTime.current = false;
                  }

                  setValue(name, finalDateTime.format("DD/MM/YYYY HH:mm"));
                  if (trigger) trigger(name); // <-- trigger validation
                }}
                format="DD/MM/YYYY HH:mm"
                value={
                  field.value && field.value !== "Invalid Date"
                    ? dayjs(field.value, "DD/MM/YYYY HH:mm")
                    : null
                }
                minDateTime={minDate ? dayjs(minDate) : null}
                maxDateTime={maxDate && !onMaxDateExceeded ? dayjs(maxDate) : null}
                sx={{
                  width: "100%",
                  fontFamily: "inherit",
                }}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1300 },
                  },
                  dialog: {
                    sx: { zIndex: 1300 },
                  },
                  desktopPaper: {
                    sx: { zIndex: 1300 },
                  },
                  textField: {
                    variant: "standard",
                    InputProps: {
                      disableUnderline: true,
                      sx: {
                        boxSizing: "border-box",
                        minHeight: "48px",
                        fontFamily: '"Schibsted Grotesk", "Segoe UI", system-ui, sans-serif',
                        fontSize: "16px",
                        color: "#0C2233",
                        border: "0",
                        borderBottom: errors
                          ? "2px solid #B3261E"
                          : "2px solid #62797A",
                        backgroundColor: errors ? "#FDF3F2" : "#EAF1F6",
                        borderRadius: "8px",
                        paddingTop: "0",
                        paddingBottom: "0",
                        paddingLeft: "15px",
                        paddingRight: "10px",
                        transition: "background-color 160ms ease",
                        "&:hover": {
                          backgroundColor: errors ? "#FDF3F2" : "#E1EBF2",
                        },
                        "& .MuiInputBase-input": {
                          padding: "0",
                          font: "inherit",
                          color: "inherit",
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "#566B6C",
                          opacity: 1,
                        },
                      },
                    },
                  },
                }}
                />
              </LocalizationProvider>
            </ThemeProvider>
          )}
        />
        {watch(name) && watch(name) !== "Invalid Date" && (
          <span
            aria-hidden="true"
            className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-[42px] tw-top-0 tw-z-[2] tw-my-auto tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-bg-dis-success tw-text-white max-[481px]:tw-right-[15px]"
          >
            <FaCheck className="tw-h-2.5 tw-w-2.5" />
          </span>
        )}
      </div>
      {errors && (
        <span
          className={`tw-mt-[5px] tw-block tw-text-sm tw-text-dis-error ${
            isInFieldStack ? "" : "tw-mb-[5px]"
          }`}
        >
          Required
        </span>
      )}
    </div>
  );
};

export default NewDateTimePicker;
